import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import NudosDatePicker from '../../../../../components/NudosComponents/NudosDatePicker';
import LogisticsServiceTrelloCard from '../../LogisticsServiceTrelloCard/LogisticsServiceTrelloCard';
import { NudosButton, NudosTextInput } from '../../../../../components/NudosComponents';
import { formatShortDate } from '../../../../../utils/formatDatesAndHours';
import {
  IdateRange,
  IlogisticServiceDetails,
  IlogisticServiceTrelloCardData,
  LOCATION_PLACE_TYPE
} from '../../../../../types/services';
import { TchangeLogisticServiceStatusCallback } from '../LogisticsServicesTrelloModals';
import { getNudosData } from '../../../../../utils/getNudosData';
import { additionalDaysForUnassignmentCollectionDate } from '../../../../../utils/productDefinitions';
import InfoDisplayItem from '../../../../LogisticServices/LogisticServiceDetailsView/Components/LogisticServiceDetailsTab1General/LogisticServiceDatesAndProgrammingDetails/InfoDisplayItem';
import './ConfirmedToActiveModalLS.scss';

const ConfirmedToActiveModalLsContent = ({
  updateDataCallback,
  modalButtonIsLoading,
  logisticServiceDetails,
  closeModalAfterSubmitCallback,
  selectedLogisticServiceDataForModal
}: {
  updateDataCallback: TchangeLogisticServiceStatusCallback;
  modalButtonIsLoading?: boolean;
  logisticServiceDetails?: IlogisticServiceDetails;
  closeModalAfterSubmitCallback: () => void;
  selectedLogisticServiceDataForModal: IlogisticServiceTrelloCardData;
}) => {
  const [trackingCode, setTrackingCode] = useState<string>();
  const [trackingLink, setTrackingLink] = useState<string>();
  const [collectionText, setCollectionText] = useState<string>();
  const [deliveryDateSince, setDeliveryDateSince] = useState<string>();
  const [deliveryDateUntil, setDeliveryDateUntil] = useState<string>();
  const [collectionDateSince, setCollectionDateSince] = useState<string>();
  const [collectionDateUntil, setCollectionDateUntil] = useState<string>();
  const [estimatedDeliveryTime, setEstimatedDeliveryTime] = useState<IdateRange>();

  const sinceCollectionDateByUser = logisticServiceDetails?.collectionInfo?.chosenCollectionDate;
  const estimatedUntilCollectionDateByUser = DateTime.fromISO(
    logisticServiceDetails?.collectionInfo?.chosenCollectionDate || ''
  )
    .plus({ days: additionalDaysForUnassignmentCollectionDate })
    .toISODate();

  const sinceDeliveryDateByUser = logisticServiceDetails?.deliveryInfo?.chosenDeliveryDate;
  const estimatedUntilDeliveryDateByUser = DateTime.fromISO(
    logisticServiceDetails?.deliveryInfo?.chosenDeliveryDate || ''
  )
    .plus({ days: additionalDaysForUnassignmentCollectionDate })
    .toISODate();
  const minCollectionDate = logisticServiceDetails?.collectionInfo?.user?.minCollectionDate;
  const maxDeliveryDate = logisticServiceDetails?.deliveryInfo?.user?.maxDeliveryDate;

  const formatSinceCollectionDateByUser = formatShortDate(sinceCollectionDateByUser || '').onlyDate;
  const formatEstimatedUntilCollectionDateByUser = formatShortDate(estimatedUntilCollectionDateByUser || '').onlyDate;
  const formatSinceDeliveryDateByUser = formatShortDate(sinceDeliveryDateByUser || '').onlyDate;
  const formatEstimatedUntilDeliveryDateByUser = formatShortDate(estimatedUntilDeliveryDateByUser || '').onlyDate;
  const formatMinCollectionDate =
    formatShortDate(logisticServiceDetails?.collectionInfo?.user?.minCollectionDate || '')?.onlyDate || '';
  const formatMaxDeliveryDate =
    formatShortDate(logisticServiceDetails?.deliveryInfo?.user?.maxDeliveryDate || '')?.onlyDate || '';

  const requireCollectionDate = [
    LOCATION_PLACE_TYPE.USER,
    LOCATION_PLACE_TYPE.OTHER,
    LOCATION_PLACE_TYPE.OFFICE
  ].includes(logisticServiceDetails?.originLocation?.place as LOCATION_PLACE_TYPE);
  const requireDeliveryDate = [
    LOCATION_PLACE_TYPE.USER,
    LOCATION_PLACE_TYPE.OTHER,
    LOCATION_PLACE_TYPE.OFFICE,
    LOCATION_PLACE_TYPE.DIAGNOSTIC,
    LOCATION_PLACE_TYPE.WARRANTY
  ].includes(logisticServiceDetails?.destinationLocation?.place as LOCATION_PLACE_TYPE);
  const requireBothDates = requireCollectionDate && requireDeliveryDate;

  const completedTrackingInformation =
    !!deliveryDateSince && !!deliveryDateUntil && !!collectionDateSince && !!collectionDateUntil;

  const estimatedDeliveryTimeText =
    estimatedDeliveryTime && estimatedDeliveryTime?.since
      ? `${estimatedDeliveryTime?.since}${
          estimatedDeliveryTime?.until ? ` - ${estimatedDeliveryTime?.until}` : ''
        } días hábiles`
      : 'Sin datos';

  const datesText = formatSinceDeliveryDateByUser
    ? `${formatSinceDeliveryDateByUser} - ${formatEstimatedUntilDeliveryDateByUser}`
    : estimatedDeliveryTimeText;

  const handleSaveButton = () => {
    const currentNudosUser = getNudosData();
    if (!currentNudosUser?.userData?.id) return;
    const body = {
      userId: currentNudosUser?.userData?.id,
      status: 'activo',
      active: {
        collectSince: collectionDateSince,
        collectUntil: collectionDateUntil,
        deliveryUntil: deliveryDateUntil,
        deliverySince: deliveryDateSince,
        trackingCode: trackingCode,
        trackingLink: trackingLink
      }
    };
    updateDataCallback(body, closeModalAfterSubmitCallback);
  };

  const handleDisabledButton = () => {
    if (requireBothDates && !completedTrackingInformation) return true;
    if (requireCollectionDate && (!collectionDateSince || !collectionDateUntil)) return true;
    if (requireDeliveryDate && (!deliveryDateSince || !deliveryDateUntil)) return true;
    return false;
  };

  const initializeFormWithExistingData = () => {
    !trackingCode && logisticServiceDetails?.trackingCode && setTrackingCode(logisticServiceDetails?.trackingCode);
    !trackingLink && logisticServiceDetails?.trackingLink && setTrackingLink(logisticServiceDetails?.trackingLink);
    !deliveryDateSince &&
      logisticServiceDetails?.estimatedDates?.delivery?.since &&
      setDeliveryDateSince(logisticServiceDetails?.estimatedDates?.delivery?.since);
    !deliveryDateUntil &&
      logisticServiceDetails?.estimatedDates?.delivery?.until &&
      setDeliveryDateUntil(logisticServiceDetails?.estimatedDates?.delivery?.until);
    !collectionDateSince &&
      logisticServiceDetails?.estimatedDates?.collect?.since &&
      setCollectionDateSince(logisticServiceDetails?.estimatedDates?.collect?.since);
    !collectionDateUntil &&
      logisticServiceDetails?.estimatedDates?.collect?.until &&
      setCollectionDateUntil(logisticServiceDetails?.estimatedDates?.collect?.until);
    !collectionText &&
      logisticServiceDetails?.collectionInfo?.collectionComment &&
      setCollectionText(logisticServiceDetails?.collectionInfo?.collectionComment);
    !collectionText &&
      !logisticServiceDetails?.collectionInfo?.collectionComment &&
      logisticServiceDetails?.deliveryInfo?.deliveryComment &&
      setCollectionText(logisticServiceDetails?.deliveryInfo?.deliveryComment);
    logisticServiceDetails?.estimatedDeliveryTime &&
      setEstimatedDeliveryTime(logisticServiceDetails?.estimatedDeliveryTime);
  };

  useEffect(() => {
    initializeFormWithExistingData();
  }, [logisticServiceDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div id="confirmedToActiveModalLS">
      <div className="confirmedToActiveModalContainer">
        <div className="modalTitle">Estás a punto de actualizar el estado del siguiente servicio:</div>
        <LogisticsServiceTrelloCard logisticServiceData={selectedLogisticServiceDataForModal} />
        <div className="modalInformationcontainer">
          <div className="modalInformation">
            <div className="modalInformationUserInformation">
              <div className="datesTitle">Información ingresada por el usuario</div>
              <div className="datesContainer">
                {minCollectionDate && (
                  <div className="iconContainer">
                    <InfoDisplayItem
                      oneToneIconProps={{
                        variant: 'user',
                        standardSize: 12
                      }}
                      text="Fecha de salida del empleado"
                      value={formatMinCollectionDate ?? ''}
                    />
                  </div>
                )}
                {maxDeliveryDate && (
                  <div className="iconContainer">
                    <InfoDisplayItem
                      oneToneIconProps={{
                        variant: 'user',
                        standardSize: 12
                      }}
                      text="Fecha de ingreso del empleado"
                      value={formatMaxDeliveryDate ?? ''}
                    />
                  </div>
                )}
                {formatSinceCollectionDateByUser && formatEstimatedUntilCollectionDateByUser && (
                  <div className="iconContainer">
                    <InfoDisplayItem
                      oneToneIconProps={{
                        variant: 'calendar',
                        standardSize: 12
                      }}
                      text={
                        maxDeliveryDate
                          ? 'Fechas solicitadas para la entrega'
                          : 'Fechas solicitadas para la recolección'
                      }
                      value={`${formatSinceCollectionDateByUser} - ${formatEstimatedUntilCollectionDateByUser}`}
                      dateCustomClassName="text-nudos-text-color"
                    />
                  </div>
                )}
                <InfoDisplayItem
                  oneToneIconProps={{
                    variant: 'calendar',
                    standardSize: 12
                  }}
                  text="Estimado de entrega"
                  value={datesText}
                  dateCustomClassName="text-nudos-text-color"
                />
              </div>
              {collectionText && (
                <div className="collectionDetails">
                  <div className="dividerStyles" />
                  <div className="collectionTitle">Solicitudes del usuario</div>
                  <div className="collectionText">{collectionText}</div>
                </div>
              )}
            </div>

            <div className="trackingInformationContainer">
              <div className="informationTitleContainer">
                <div className="informationTitle">Información de tracking</div>
              </div>
              {requireCollectionDate && (
                <div className="containerLine">
                  <NudosDatePicker
                    label="Fecha estimada de recolección (Desde)*"
                    isFilled={!!collectionDateSince}
                    defaultValue={collectionDateSince}
                    desiredTimeZone={-6} // Zona Horaria de México por definición
                    customClassName="customInput"
                    updateCurrentValueCallback={setCollectionDateSince}
                  />
                  <NudosDatePicker
                    label="Fecha estimada de recolección (Hasta)*"
                    isFilled={!!collectionDateUntil}
                    defaultValue={collectionDateUntil}
                    desiredTimeZone={-6} // Zona Horaria de México por definición
                    customClassName="customInput"
                    updateCurrentValueCallback={setCollectionDateUntil}
                  />
                </div>
              )}
              {requireDeliveryDate && (
                <div className="containerLine">
                  <NudosDatePicker
                    label="Fecha estimada de entrega (Desde)*"
                    isFilled={!!deliveryDateSince}
                    defaultValue={deliveryDateSince}
                    desiredTimeZone={-6} // Zona Horaria de México por definición
                    customClassName="customInput"
                    updateCurrentValueCallback={setDeliveryDateSince}
                  />
                  <NudosDatePicker
                    label="Fecha estimada de entrega (Hasta)*"
                    isFilled={!!deliveryDateUntil}
                    defaultValue={deliveryDateUntil}
                    desiredTimeZone={-6} // Zona Horaria de México por definición
                    customClassName="customInput"
                    updateCurrentValueCallback={setDeliveryDateUntil}
                  />
                </div>
              )}
              <div className="containerLine">
                <NudosTextInput
                  label="Link para tracking del servicio (opcional)"
                  isFilled={!!trackingLink}
                  placeholder="Pega el link de tracking"
                  defaultValue={trackingLink}
                  handleChange={setTrackingLink}
                  customClassName="customInput"
                />
                <NudosTextInput
                  label="Código/guía de rastreo (opcional)"
                  isFilled={!!trackingCode}
                  placeholder="Escribe el código/guía aquí"
                  defaultValue={trackingCode}
                  handleChange={setTrackingCode}
                  customClassName="customInput"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="containerButton">
          <NudosButton
            buttonText="Guardar"
            handleClick={handleSaveButton}
            customClassName="firstButton"
            isButtonLoading={modalButtonIsLoading}
            isButtonDisabled={handleDisabledButton()}
          />
        </div>
      </div>
    </div>
  );
};
export default ConfirmedToActiveModalLsContent;
